import { getDb } from "@/services/storage/sqlite/connection";
import { floatsToBytes } from "@/services/vector/encoding";
import { createId } from "@/utils";
import type { Collection, DocumentStatus, KnowledgeDocument } from "@/types";

interface CollectionRow {
  id: string;
  name: string;
  created_at: number;
  workspace_id: string | null;
}

interface DocumentRow {
  id: string;
  collection_id: string | null;
  name: string;
  source_path: string;
  file_type: string;
  size_bytes: number;
  imported_at: number;
  status: DocumentStatus;
  error: string | null;
  tags_json: string;
  chunk_count: number;
}

function rowToCollection(row: CollectionRow): Collection {
  return { id: row.id, name: row.name, createdAt: row.created_at, workspaceId: row.workspace_id };
}

function rowToDocument(row: DocumentRow): KnowledgeDocument {
  return {
    id: row.id,
    collectionId: row.collection_id,
    name: row.name,
    sourcePath: row.source_path,
    fileType: row.file_type,
    sizeBytes: row.size_bytes,
    importedAt: row.imported_at,
    status: row.status,
    error: row.error,
    tags: JSON.parse(row.tags_json) as string[],
    chunkCount: row.chunk_count,
  };
}

const DOCUMENT_SELECT = `SELECT d.*, (SELECT COUNT(*) FROM chunks c WHERE c.document_id = d.id) AS chunk_count
                          FROM documents d`;

export const knowledgeRepository = {
  async listCollections(): Promise<Collection[]> {
    const db = await getDb();
    const rows = await db.select<CollectionRow[]>("SELECT * FROM collections ORDER BY created_at ASC");
    return rows.map(rowToCollection);
  },

  async createCollection(name: string, workspaceId: string | null = null): Promise<Collection> {
    const db = await getDb();
    const collection: Collection = { id: createId("col"), name, createdAt: Date.now(), workspaceId };
    await db.execute("INSERT INTO collections (id, name, created_at, workspace_id) VALUES ($1, $2, $3, $4)", [
      collection.id,
      collection.name,
      collection.createdAt,
      collection.workspaceId,
    ]);
    return collection;
  },

  async renameCollection(id: string, name: string): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE collections SET name = $1 WHERE id = $2", [name, id]);
  },

  async deleteCollection(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM collections WHERE id = $1", [id]);
  },

  async listDocuments(collectionId?: string): Promise<KnowledgeDocument[]> {
    const db = await getDb();
    const rows = collectionId
      ? await db.select<DocumentRow[]>(`${DOCUMENT_SELECT} WHERE d.collection_id = $1 ORDER BY d.imported_at DESC`, [
          collectionId,
        ])
      : await db.select<DocumentRow[]>(`${DOCUMENT_SELECT} ORDER BY d.imported_at DESC`);
    return rows.map(rowToDocument);
  },

  async getDocument(id: string): Promise<KnowledgeDocument | null> {
    const db = await getDb();
    const rows = await db.select<DocumentRow[]>(`${DOCUMENT_SELECT} WHERE d.id = $1`, [id]);
    return rows[0] ? rowToDocument(rows[0]) : null;
  },

  async createDocument(input: {
    name: string;
    sourcePath: string;
    fileType: string;
    sizeBytes: number;
    collectionId: string | null;
  }): Promise<KnowledgeDocument> {
    const db = await getDb();
    const document: KnowledgeDocument = {
      id: createId("doc"),
      collectionId: input.collectionId,
      name: input.name,
      sourcePath: input.sourcePath,
      fileType: input.fileType,
      sizeBytes: input.sizeBytes,
      importedAt: Date.now(),
      status: "queued",
      error: null,
      tags: [],
      chunkCount: 0,
    };
    await db.execute(
      `INSERT INTO documents (id, collection_id, name, source_path, file_type, size_bytes, imported_at, status, error, tags_json)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)`,
      [
        document.id,
        document.collectionId,
        document.name,
        document.sourcePath,
        document.fileType,
        document.sizeBytes,
        document.importedAt,
        document.status,
        document.error,
        JSON.stringify(document.tags),
      ],
    );
    await db.execute(
      "INSERT INTO processing_queue (id, document_id, status, attempts, last_error, updated_at) VALUES ($1, $2, $3, 0, NULL, $4)",
      [createId("job"), document.id, "queued", Date.now()],
    );
    return document;
  },

  async updateDocumentStatus(id: string, status: DocumentStatus, error: string | null = null): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE documents SET status = $1, error = $2 WHERE id = $3", [status, error, id]);
  },

  async setDocumentCollection(id: string, collectionId: string | null): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE documents SET collection_id = $1 WHERE id = $2", [collectionId, id]);
  },

  async setDocumentTags(id: string, tags: string[]): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE documents SET tags_json = $1 WHERE id = $2", [JSON.stringify(tags), id]);
  },

  async renameDocument(id: string, name: string): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE documents SET name = $1 WHERE id = $2", [name, id]);
  },

  async deleteDocument(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM documents WHERE id = $1", [id]);
  },

  async replaceChunks(
    documentId: string,
    chunks: { seq: number; content: string; embedding: Float32Array }[],
  ): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM chunks WHERE document_id = $1", [documentId]);
    for (const chunk of chunks) {
      await db.execute("INSERT INTO chunks (id, document_id, seq, content, embedding) VALUES ($1, $2, $3, $4, $5)", [
        createId("chunk"),
        documentId,
        chunk.seq,
        chunk.content,
        floatsToBytes(chunk.embedding),
      ]);
    }
  },

  // --- Processing queue -------------------------------------------------

  async setQueueStatus(documentId: string, status: string, lastError: string | null = null): Promise<void> {
    const db = await getDb();
    await db.execute(
      "UPDATE processing_queue SET status = $1, last_error = $2, updated_at = $3 WHERE document_id = $4",
      [status, lastError, Date.now(), documentId],
    );
  },

  async incrementAttempts(documentId: string): Promise<number> {
    const db = await getDb();
    await db.execute("UPDATE processing_queue SET attempts = attempts + 1, updated_at = $1 WHERE document_id = $2", [
      Date.now(),
      documentId,
    ]);
    const rows = await db.select<{ attempts: number }[]>(
      "SELECT attempts FROM processing_queue WHERE document_id = $1",
      [documentId],
    );
    return rows[0]?.attempts ?? 0;
  },

  async listInterruptedDocumentIds(): Promise<string[]> {
    const db = await getDb();
    const rows = await db.select<{ document_id: string }[]>(
      "SELECT document_id FROM processing_queue WHERE status IN ('queued', 'processing')",
    );
    return rows.map((r) => r.document_id);
  },
};
